/**
 * Play one range of a video frame-tight, the way the Analyser does: the seek aims one millisecond inside
 * the in edge, playback begins once the seek lands, and a per-frame watch stops on the last kept frame.
 * The transport's own pause holds the range and resumes it; a person's pause or scrub on the player's
 * controls ends the watch, so the range never takes the controls away. Cue shows a range's first frame
 * without playing it.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { EDGE_INSET_MS, FRAME_WATCH_LEAD_MS, insideEdge } from '@/lib/playback';

export function useRangePlayback(videoRef: RefObject<HTMLVideoElement | null>) {
  const watch = useRef<{ raf: number; detach: () => void } | null>(null);
  const pending = useRef<(() => void) | null>(null);
  /** Set while the transport pauses on purpose, so the pause listener keeps the range. */
  const holding = useRef(false);

  const stop = () => {
    if (watch.current) {
      cancelAnimationFrame(watch.current.raf);
      watch.current.detach();
      watch.current = null;
    }
    const v = videoRef.current;
    if (v && pending.current) v.removeEventListener('seeked', pending.current);
    pending.current = null;
  };
  useEffect(() => stop, []);

  const playRange = (fromMs: number, untilMs: number) => {
    const v = videoRef.current;
    if (!v || untilMs <= fromMs) return;
    stop();
    v.pause();
    const begin = () => {
      pending.current = null;
      const touched = () => {
        if (holding.current) holding.current = false;
        else stop();
      };
      const tick = () => {
        const w = watch.current;
        if (!w) return;
        if (v.currentTime * 1000 >= untilMs - FRAME_WATCH_LEAD_MS) {
          // The tick can land a frame late; the seek settles the picture on the last kept frame.
          stop();
          v.pause();
          v.currentTime = (untilMs - EDGE_INSET_MS) / 1000;
          return;
        }
        w.raf = requestAnimationFrame(tick);
      };
      v.addEventListener('pause', touched);
      v.addEventListener('seeking', touched);
      watch.current = {
        raf: requestAnimationFrame(tick),
        detach: () => {
          v.removeEventListener('pause', touched);
          v.removeEventListener('seeking', touched);
        },
      };
      v.play().catch(() => stop());
    };
    if (Math.abs(v.currentTime * 1000 - insideEdge(fromMs)) < 1) begin();
    else {
      pending.current = begin;
      v.addEventListener('seeked', begin, { once: true });
      v.currentTime = insideEdge(fromMs) / 1000;
    }
  };

  /** Pause, or carry on from where it paused; a range in play keeps its out point. */
  const togglePause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => stop());
      return;
    }
    if (watch.current) holding.current = true;
    v.pause();
  };

  /** Stop and show the range's first frame, without playing it. */
  const cue = (fromMs: number) => {
    const v = videoRef.current;
    stop();
    if (!v) return;
    v.pause();
    v.currentTime = insideEdge(fromMs) / 1000;
  };

  return { playRange, togglePause, cue, stop };
}
